import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CIHealthBadge from '../../components/admin/CIHealthBadge';
import PostmarkBudgetCard from '../../components/admin/PostmarkBudgetCard';
import PytestFlakeCard from '../../components/admin/PytestFlakeCard';
import TestMondayEmailButton from '../../components/admin/TestMondayEmailButton';
import CommandCentrePalette from '../../components/admin/CommandCentrePalette';
import CIRedAlertBanner from '../../components/admin/CIRedAlertBanner';
import TrustReportEngagementCard from '../../components/admin/TrustReportEngagementCard';
import AdminLayout from '../../components/layout/AdminLayout';
import CheatSheet from '../../components/admin/CheatSheet';
import SecurityStatusCard from '../../components/admin/SecurityStatusCard';
import SubscriptionEventsFeed from '../../components/admin/SubscriptionEventsFeed';
import RecentAdminActivityCard from '../../components/admin/RecentAdminActivityCard';
import LiveTractionCard from '../../components/admin/LiveTractionCard';
import FounderLetterEditor from '../../components/admin/FounderLetterEditor';
import BoardDeckCard from '../../components/admin/BoardDeckCard';
import PersonaSplitTile from '../../components/admin/PersonaSplitTile';
import StabilisationLog from '../../components/admin/StabilisationLog';
import StripeLiveBanner from '../../components/admin/StripeLiveBanner';
import ProspectAlertsCard from '../../components/admin/ProspectAlertsCard';
import InvestorHeatmapCard from '../../components/admin/InvestorHeatmapCard';
import MagicLinkHealthCard from '../../components/admin/MagicLinkHealthCard';
import PeerHelpsCard from '../../components/admin/PeerHelpsCard';
import MayaManagerTile from '../../components/admin/MayaManagerTile';
import TemplatesFiredCard from '../../components/admin/TemplatesFiredCard';
import ActiveLaunchFunnelCard from '../../components/admin/ActiveLaunchFunnelCard';
import PrivacyShieldHitsCard from '../../components/admin/PrivacyShieldHitsCard';
import PlatformHealthCard from '../../components/admin/PlatformHealthCard';
import TriggeredDeclinedTile from '../../components/admin/TriggeredDeclinedTile';
import InvestorPulseSchedulerCard from '../../components/admin/InvestorPulseSchedulerCard';
import NewsletterOperationsCard from '../../components/admin/NewsletterOperationsCard';
import NewsletterQueueStack from '../../components/admin/NewsletterQueueStack';
import NewsletterAnnualCalendar from '../../components/admin/NewsletterAnnualCalendar';
import PostmarkSuppressionsTile from '../../components/admin/PostmarkSuppressionsTile';
import CommandCentreSubNav from '../../components/admin/CommandCentreSubNav';
import AvABehaviorStudio from '../../components/admin/AvABehaviorStudio';
import RebeccaManagerTile from '../../components/admin/RebeccaManagerTile';
import NeoManagerTile from '../../components/admin/NeoManagerTile';
import ProactivityLeaderboardTile from '../../components/admin/ProactivityLeaderboardTile';
import VoiceOfUserCard from '../../components/admin/VoiceOfUserCard';
import WhitePaperLedgerCard from '../../components/admin/WhitePaperLedgerCard';
import RealWorldActivityPulseCard from '../../components/admin/RealWorldActivityPulseCard';
import InfoTooltipWrapper from '../../components/dashboard/InfoTooltipWrapper';
import { CommandCentreSection } from '../../components/admin/CommandCentreSection';
import { CC_PRODUCT_DESCRIPTIONS as CC } from '../../components/admin/ccProductDescriptions';
import './AdminHome.css';
import {
  Users, TrendingUp, DollarSign, Shield, Activity, Award, Target,
  Crosshair, ArrowUpRight, ArrowDownRight, Loader2, Heart, Flame,
  AlertTriangle, CheckCircle, XCircle, Clock, Gauge, ThumbsUp, Zap,
  Bell, Info, TriangleAlert, BookOpen, Music, Swords, MapPin, UserCheck,
  HeartPulse, Wrench, Sparkles, Send, Megaphone, Compass, ShieldCheck,
  Globe,
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  RadialBarChart, RadialBar
} from 'recharts';
import { apiClient } from '../../api/client';

const SEGMENT_COLORS = {
  adults: '#F5831F',
  teens: '#7B4FD8',
  schools: '#2563EB',
  business: '#2EAA5C',
};
const CHART_COLORS = ['#F5831F', '#7B4FD8', '#2563EB', '#2EAA5C', '#DC2626', '#F59E0B'];

const MetricCard = ({ icon: Icon, label, value, trend, trendLabel, color = '#F5831F', sub, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white p-5 rounded-lg group transition-all"
    style={{ border: '1px solid #E0E0E0', cursor: onClick ? 'pointer' : 'default' }}
    data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs" style={{ color: '#44403A' }}>{label}</p>
        <p className="text-3xl font-bold mt-1" style={{ color: '#1A1209' }}>{value}</p>
        {sub && <p className="text-xs mt-1" style={{ color: '#44403A' }}>{sub}</p>}
      </div>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon size={20} style={{ color }} />
      </div>
    </div>
    {trend !== undefined && (
      <div className="flex items-center gap-1 mt-3">
        {trend >= 0 ? <ArrowUpRight size={14} className="text-green-600" /> : <ArrowDownRight size={14} className="text-red-500" />}
        <span className={`text-xs font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>{Math.abs(trend)}%</span>
        <span className="text-xs" style={{ color: '#44403A' }}>{trendLabel || 'vs last 7 days'}</span>
      </div>
    )}
  </div>
);

const SegmentCard = ({ name, color, total, activeWeek, freeCount, paidCount, churnPositive, churnStandard, onClick }) => (
  <div onClick={onClick} className="bg-white p-5 rounded-lg transition-all hover:shadow-md" style={{ border: '1px solid #E0E0E0', borderTop: `3px solid ${color}`, cursor: 'pointer' }}>
    <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>{name}</h3>
    <p className="text-2xl font-bold mt-1" style={{ color: '#1A1209' }}>{total}</p>
    <p className="text-xs" style={{ color: '#44403A' }}>Active this week: {activeWeek}</p>
    <div className="mt-3 h-16">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={[{ name: 'Free', value: Math.max(freeCount, 1) }, { name: 'Paid', value: paidCount }]} dataKey="value" cx="50%" cy="50%" outerRadius={25} innerRadius={15}>
            <Cell fill="#E5E7EB" /><Cell fill={color} />
          </Pie>
          <Tooltip formatter={(v, n) => [`${v} users`, n]} />
        </PieChart>
      </ResponsiveContainer>
    </div>
    <div className="flex gap-3 mt-2 text-xs">
      <span style={{ color: '#2EAA5C' }}>Positive: {churnPositive}</span>
      <span style={{ color: '#44403A' }}>Standard: {churnStandard}</span>
    </div>
  </div>
);

const AdminHome = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [overview, trends, revenue, funnel, cohorts, activeHours, health, guardrails, engagement, leads, atRisk, retentionCohorts, healthScore, positiveChurn, leadAlerts, geography] = await Promise.all([
        apiClient.request('/api/admin/overview'),
        apiClient.request('/api/admin/trends'),
        apiClient.request('/api/admin/revenue'),
        apiClient.request('/api/admin/funnel'),
        apiClient.request('/api/admin/cohorts'),
        apiClient.request('/api/admin/active-hours'),
        apiClient.request('/api/admin/system/health'),
        apiClient.request('/api/admin/guardrails/events?limit=5'),
        apiClient.request('/api/admin/engagement-summary').catch(() => ({})),
        apiClient.request('/api/admin/leads/summary').catch(() => ({ total_this_week: 0, qualified: 0, conversion_rate: 0, sources: [], connection_status: 'disconnected' })),
        apiClient.request('/api/admin/at-risk-users').catch(() => ({ at_risk_users: [], total_at_risk: 0, high_risk: 0, medium_risk: 0 })),
        apiClient.request('/api/admin/retention-cohorts').catch(() => ({ cohorts: [], total_weeks: 8 })),
        apiClient.request('/api/admin/business-health-score').catch(() => ({ score: 0, grade: 'N/A', pillars: {}, trends: {} })),
        apiClient.request('/api/admin/positive-churn').catch(() => ({ positive_churn: [], standard_churn: [], summary: { total_churned: 0, positive_count: 0, standard_count: 0, positive_rate: 0 } })),
        apiClient.request('/api/admin/leads/alerts').catch(() => ({ alerts: [], stats: {}, high_quality_leads: [], top_keywords: [] })),
        apiClient.request('/api/admin/user-geography').catch(() => ({ total_users: 0, by_timezone: [], by_age_band: [], by_role: [], by_region: [] })),
      ]);
      setData({ overview, trends: trends.trends || [], revenue, funnel: funnel.funnel || [], cohorts: cohorts.cohorts || [], activeHours: activeHours.hours || [], health, guardrails, engagement, leads, atRisk, retentionCohorts, healthScore, positiveChurn, leadAlerts, geography });
    } catch (e) { console.error('Admin data load failed:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { const i = setInterval(loadData, 30000); return () => clearInterval(i); }, [loadData]);

  if (loading || !data) {
    return <AdminLayout pageTitle="Command Centre">
      <CheatSheet pageId="analytics" /><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#F5831F' }} /></div></AdminLayout>;
  }

  const { overview, trends, revenue, health, guardrails, engagement, leads, atRisk, retentionCohorts, healthScore, positiveChurn, leadAlerts, geography } = data;
  const u = overview.users, s = overview.subscriptions, e = overview.engagement;
  const sd = overview.self_development || {};

  return (
    <AdminLayout pageTitle="Command Centre">
      <div className="command-centre-home">
      <CheatSheet pageId="analytics" />

      {/* Top-of-page red banner — only renders when pytest is failing. The
          nightly worker is silent by design; this banner is the visual
          escalation so a regression can't slip past without notice. */}
      <CIRedAlertBanner />

      {/* Sticky sub-nav — quick-jump links to the 5 highest-traffic sections. */}
      <CommandCentreSubNav />
      <CommandCentrePalette />

      <CommandCentreSection
        title="Business Intelligence"
        subtitle="Headline metrics, magic-link health, user segments, at-risk users, business health score, and the retention cohort heatmap. The numbers investors and the board most often ask about."
        accent="#4338CA"
        icon={<Gauge className="w-4 h-4" strokeWidth={2} />}
        testId="cc-section-business-intelligence"
      >
      {/* Row 1 — Headline Numbers */}
      <InfoTooltipWrapper persistent id="cc-business-glance" {...CC['business-at-a-glance']} testId="business-at-a-glance">
        <h2 className="text-lg font-bold mb-3" style={{ color: '#1A1209' }}>Business at a Glance</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <MetricCard icon={Users} label="Total Active Users" value={u.total} trend={u.mom_growth_pct ?? 0} trendLabel="MoM (same formula as Pulse)" color="#F5831F" onClick={() => navigate('/admin/users')} />
          <MetricCard icon={Globe} label="Global Analytics" value="🌍" sub="Countries, cities, heatmap, pages" color="#7B4FD8" onClick={() => navigate('/admin/global-analytics')} />
          <MetricCard icon={TrendingUp} label="LinkedIn Campaign" value="📣" sub="Sept 16 launch funnel + Founding Families" color="#0A66C2" onClick={() => navigate('/admin/campaign')} />
          <MetricCard icon={TrendingUp} label="New Sign-ups" value={u.new_this_period} color="#7B4FD8" sub="Last 30 days" />
          <MetricCard icon={DollarSign} label="Total Revenue (MRR)" value={`£${revenue?.mrr || 0}`} color="#2EAA5C" sub={revenue?.mrr === 0 ? 'Payments LIVE (Stripe + Google Play) — no active paid subscriptions' : `ARR: £${revenue?.arr}`} onClick={() => navigate('/admin/revenue')} />
          <MetricCard icon={Activity} label="Active Users (30d)" value={u.mau_30d ?? 0} sub={`MAU, matches the Pulse · ${e.sessions_this_period} chat sessions in 30d`} color="#2563EB" />
          <MetricCard icon={Shield} label="Safety Events This Week" value={overview.safety.guardrail_blocks} color={overview.safety.guardrail_blocks > 10 ? '#DC2626' : '#F59E0B'} onClick={() => navigate('/admin/safety')} />
        </div>
      </InfoTooltipWrapper>

      {/* Magic-Link Health (Sprint 200) */}
      <InfoTooltipWrapper persistent {...CC['magic-link-health']} testId="magic-link-health">
        <h2 className="text-lg font-bold mb-3" style={{ color: '#1A1209' }}>Magic-Link Health</h2>
        <MagicLinkHealthCard />
      </InfoTooltipWrapper>

      {/* Row 2 — User Segments */}
      <InfoTooltipWrapper persistent {...CC['user-segments']} testId="user-segments">
        <h2 className="text-lg font-bold mb-3" style={{ color: '#1A1209' }}>User Segments</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <SegmentCard name="B2C Adults" color={SEGMENT_COLORS.adults} total={u.b2c_adults} activeWeek={u.new_this_period} freeCount={Math.max(u.b2c_adults - s.plus_monthly - s.plus_annual, 0)} paidCount={s.plus_monthly + s.plus_annual} churnPositive={0} churnStandard={s.churn} onClick={() => navigate('/admin/users?segment=b2c_adult')} />
          <SegmentCard name="B2C Teens" color={SEGMENT_COLORS.teens} total={u.b2c_teens} activeWeek={0} freeCount={u.b2c_teens} paidCount={0} churnPositive={0} churnStandard={0} onClick={() => navigate('/admin/users?segment=b2c_teen')} />
          <SegmentCard name="B2B Schools" color={SEGMENT_COLORS.schools} total={u.b2b_schools} activeWeek={0} freeCount={u.b2b_schools} paidCount={0} churnPositive={0} churnStandard={0} onClick={() => navigate('/admin/users?segment=b2b_school')} />
          <SegmentCard name="B2B Business" color={SEGMENT_COLORS.business} total={0} activeWeek={0} freeCount={0} paidCount={0} churnPositive={0} churnStandard={0} onClick={() => navigate('/admin/users?segment=b2b_business')} />
        </div>
      </InfoTooltipWrapper>

      {/* Row 2.5 — At Risk Users */}
      {atRisk.total_at_risk > 0 && (
        <InfoTooltipWrapper persistent {...CC['at-risk-users']} testId="at-risk-users">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#DC2626' }}>At-Risk Users</h2>
          <div className="bg-white rounded-lg" style={{ border: '1px solid #E0E0E0', borderTop: '3px solid #DC2626' }}>
            <div className="p-4 flex items-center justify-between border-b" style={{ borderColor: '#E0E0E0' }}>
              <div className="flex gap-6">
                <div><span className="text-2xl font-bold" style={{ color: '#DC2626' }}>{atRisk.high_risk}</span><span className="text-xs ml-1" style={{ color: '#44403A' }}>high risk</span></div>
                <div><span className="text-2xl font-bold" style={{ color: '#F59E0B' }}>{atRisk.medium_risk}</span><span className="text-xs ml-1" style={{ color: '#44403A' }}>medium risk</span></div>
              </div>
              <button onClick={() => navigate('/admin/users?segment=')} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: '#DC2626' }}>View All</button>
            </div>
            <div className="divide-y" style={{ borderColor: '#E0E0E0' }}>
              {atRisk.at_risk_users.slice(0, 5).map((user, i) => (
                <div key={i} className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`w-2 h-2 rounded-full ${user.risk_level === 'high' ? 'bg-red-500' : 'bg-amber-500'}`} />
                    <div>
                      <p className="text-sm font-medium" style={{ color: '#333333' }}>{user.name || user.email}</p>
                      <p className="text-xs" style={{ color: '#44403A' }}>{user.risk_reason}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs" style={{ color: '#44403A' }}>Last active: {user.last_session?.slice(0, 10)}</p>
                    <p className="text-xs" style={{ color: '#44403A' }}>{user.total_sessions} sessions total</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </InfoTooltipWrapper>
      )}

      {/* ─── TIER 2: Business Health Score ─────────────────────────── */}
      <InfoTooltipWrapper persistent {...CC['business-health-score']} testId="business-health-score">
        <h2 className="text-lg font-bold mb-3" style={{ color: '#1A1209' }}>Business Health Score</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {/* Score Gauge */}
          <div className="bg-white p-5 rounded-lg col-span-1" style={{ border: '1px solid #E0E0E0' }}>
            <div className="flex flex-col items-center">
              <div className="relative w-40 h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="90%" startAngle={180} endAngle={0} data={[{ value: healthScore.score, fill: healthScore.score >= 80 ? '#2EAA5C' : healthScore.score >= 60 ? '#F5831F' : healthScore.score >= 40 ? '#F59E0B' : '#DC2626' }]} barSize={12}>
                    <RadialBar background clockWise dataKey="value" cornerRadius={6} domain={[0, 100]} />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ top: '-10px' }}>
                  <span className="text-4xl font-bold" style={{ color: '#1A1209' }}>{healthScore.score}</span>
                  <span className="text-xs font-medium" style={{ color: healthScore.score >= 60 ? '#2EAA5C' : '#F59E0B' }}>{healthScore.grade}</span>
                </div>
              </div>
              <p className="text-xs mt-1" style={{ color: '#44403A' }}>out of 100</p>
            </div>
          </div>

          {/* Pillar Breakdown */}
          <div className="bg-white p-5 rounded-lg col-span-2" style={{ border: '1px solid #E0E0E0' }}>
            <h3 className="text-sm font-bold mb-4" style={{ color: '#1A1209' }}>Score Pillars</h3>
            <div className="space-y-4">
              {Object.entries(healthScore.pillars || {}).map(([key, pillar]) => {
                const colors = { growth: '#7B4FD8', engagement: '#F5831F', retention: '#2563EB', revenue: '#2EAA5C' };
                const icons = { growth: TrendingUp, engagement: Flame, retention: Users, revenue: DollarSign };
                const PIcon = icons[key] || Activity;
                return (
                  <div key={key} data-testid={`health-pillar-${key}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <PIcon size={14} style={{ color: colors[key] }} />
                        <span className="text-sm font-medium capitalize" style={{ color: '#333333' }}>{key}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold" style={{ color: '#1A1209' }}>{pillar.score}/{pillar.max}</span>
                      </div>
                    </div>
                    <div className="w-full h-2 rounded-full" style={{ backgroundColor: '#F0F0F0' }}>
                      <div className="h-2 rounded-full transition-all duration-500" style={{ width: `${(pillar.score / pillar.max) * 100}%`, backgroundColor: colors[key] }} />
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: '#6B6257' }}>{pillar.detail}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </InfoTooltipWrapper>

      {/* ─── TIER 2: Retention Cohort Heatmap ──────────────────────── */}
      <InfoTooltipWrapper persistent id="cc-user-cohorts" {...CC['retention-cohort-heatmap']} testId="retention-cohort-heatmap">
        <h2 className="text-lg font-bold mb-3" style={{ color: '#1A1209' }}>Retention Cohort Heatmap</h2>
        <div className="bg-white p-5 rounded-lg overflow-x-auto" style={{ border: '1px solid #E0E0E0' }}>
          {retentionCohorts.cohorts && retentionCohorts.cohorts.length > 0 ? (
            <table className="w-full text-xs" data-testid="cohort-heatmap-table">
              <thead>
                <tr>
                  <th className="text-left py-2 px-3 font-bold" style={{ color: '#333', minWidth: '100px' }}>Cohort</th>
                  <th className="text-center py-2 px-3 font-medium" style={{ color: '#666' }}>Size</th>
                  {Array.from({ length: 8 }, (_, i) => (
                    <th key={i} className="text-center py-2 px-2 font-medium" style={{ color: '#666', minWidth: '60px' }}>Wk {i}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {retentionCohorts.cohorts.map((cohort, idx) => (
                  <tr key={idx} className="border-t" style={{ borderColor: '#F0F0F0' }}>
                    <td className="py-2 px-3 font-medium" style={{ color: '#333' }}>{cohort.cohort_label}</td>
                    <td className="py-2 px-3 text-center font-medium" style={{ color: '#666' }}>{cohort.cohort_size}</td>
                    {Array.from({ length: 8 }, (_, weekIdx) => {
                      const cell = cohort.retention?.[weekIdx];
                      if (!cell && cohort.cohort_size === 0) {
                        return <td key={weekIdx} className="py-2 px-2 text-center"><span className="inline-block w-full py-1 rounded" style={{ backgroundColor: '#F5F5F5', color: '#CCC' }}>—</span></td>;
                      }
                      if (!cell) {
                        return <td key={weekIdx} className="py-2 px-2 text-center"><span className="inline-block w-full py-1 rounded" style={{ backgroundColor: '#F5F5F5', color: '#CCC' }}>—</span></td>;
                      }
                      const pct = cell.pct;
                      const bg = pct >= 80 ? '#059669' : pct >= 60 ? '#10B981' : pct >= 40 ? '#F59E0B' : pct >= 20 ? '#F97316' : pct > 0 ? '#EF4444' : '#F5F5F5';
                      const textColor = pct >= 20 ? '#FFFFFF' : pct > 0 ? '#FFFFFF' : '#CCC';
                      return (
                        <td key={weekIdx} className="py-2 px-2 text-center">
                          <span className="inline-block w-full py-1 rounded font-bold text-xs" style={{ backgroundColor: bg, color: textColor }}>
                            {pct > 0 ? `${pct}%` : '—'}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-center py-8" style={{ color: '#6B6257' }}>No cohort data available yet. Users need to sign up over multiple weeks to see retention patterns.</p>
          )}
          <div className="mt-3 flex items-center gap-4 text-xs" style={{ color: '#666' }}>
            <span className="font-medium">Legend:</span>
            {[{ label: '80%+', color: '#059669' }, { label: '60-79%', color: '#10B981' }, { label: '40-59%', color: '#F59E0B' }, { label: '20-39%', color: '#F97316' }, { label: '<20%', color: '#EF4444' }].map(l => (
              <span key={l.label} className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: l.color }} />{l.label}</span>
            ))}
          </div>
        </div>
      </InfoTooltipWrapper>
      </CommandCentreSection>

      <CommandCentreSection
        title="Revenue, Social & Product Depth"
        subtitle="Revenue + subscriptions, social growth engagement, self-development feature usage, and safety-system health. The deep-dive dashboards for weekly review and board prep."
        accent="#BE185D"
        icon={<DollarSign className="w-4 h-4" strokeWidth={2} />}
        testId="cc-section-revenue-social-product"
      >
      {/* Row 4 — Revenue & Subscriptions */}
      <InfoTooltipWrapper persistent {...CC['revenue-and-subscriptions']} testId="revenue-and-subscriptions">
        <h2 className="text-lg font-bold mb-3" style={{ color: '#1A1209' }}>Revenue & Subscriptions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Revenue Card */}
          <div className="bg-white p-5 rounded-lg cursor-pointer hover:shadow-md transition-all" style={{ border: '1px solid #E0E0E0' }} onClick={() => navigate('/admin/revenue')}>
            <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Revenue</h3>
            <div className="flex gap-6 mb-3">
              <div><p className="text-xs" style={{ color: '#44403A' }}>MRR</p><p className="text-xl font-bold" style={{ color: '#1A1209' }}>£{revenue?.mrr || 0}</p></div>
              <div><p className="text-xs" style={{ color: '#44403A' }}>ARR</p><p className="text-xl font-bold" style={{ color: '#1A1209' }}>${revenue?.arr || 0}</p></div>
            </div>
            {revenue?.mrr === 0 && <p className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 inline-block mb-3">Payments LIVE (Stripe + Google Play) — no active paid subscriptions</p>}
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.trends}>
                  <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis />
                  <Tooltip /><Area type="monotone" dataKey="sessions" name="Activity" stroke="#2EAA5C" fill="#2EAA5C" fillOpacity={0.2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span style={{ color: '#44403A' }}>Starter (Free)</span><span className="font-medium">{s.starter} users</span></div>
              <div className="flex justify-between"><span style={{ color: '#44403A' }}>Plus (£28/mo)</span><span className="font-medium">{s.plus_monthly} × £28 = £{(s.plus_monthly * 28).toFixed(2)}</span></div>
              <div className="flex justify-between"><span style={{ color: '#44403A' }}>Annual (£188/yr)</span><span className="font-medium">{s.plus_annual} × £188 = £{(s.plus_annual * 188).toFixed(2)}</span></div>
            </div>
          </div>

          {/* Subscription Health Card */}
          <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
            <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Subscription Health</h3>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={[
                    { name: 'Free', value: Math.max(u.total - s.plus_monthly - s.plus_annual, 1) },
                    { name: 'Plus Monthly', value: Math.max(s.plus_monthly, 0) },
                    { name: 'Plus Annual', value: Math.max(s.plus_annual, 0) },
                  ]} dataKey="value" cx="50%" cy="50%" outerRadius={60} innerRadius={35} label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}>
                    <Cell fill="#E5E7EB" /><Cell fill="#7B4FD8" /><Cell fill="#F5831F" />
                  </Pie>
                  <Tooltip /><Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span style={{ color: '#44403A' }}>Hearts Purchased (all time)</span><span className="font-medium">{e.hearts_purchased}</span></div>
              <div className="flex justify-between"><span style={{ color: '#44403A' }}>Total Challenges</span><span className="font-medium">{e.total_challenges}</span></div>
            </div>
          </div>

          {/* Subscription Events Feed (audit trail → Command Centre) */}
          <SubscriptionEventsFeed />
        </div>
      </InfoTooltipWrapper>

      {/* Row 5 — Engagement */}
      <InfoTooltipWrapper persistent {...CC['social-growth-engagement']} testId="social-growth-engagement">
        <h2 className="text-lg font-bold mb-3" style={{ color: '#1A1209' }}>Social Growth & Engagement</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {/* Streak Health */}
          <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
            <div className="flex items-center gap-2 mb-3"><Flame size={18} style={{ color: '#F5831F' }} /><h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Streak Health</h3></div>
            <p className="text-2xl font-bold" style={{ color: '#1A1209' }}>{engagement.avg_streak || 0}</p>
            <p className="text-xs" style={{ color: '#44403A' }}>Average streak length (days)</p>
            <p className="text-sm mt-2" style={{ color: '#44403A' }}>{engagement.streak_7plus || 0}% of users on 7+ day streak</p>
            {engagement.streak_distribution && (
              <div className="mt-3 h-24">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={engagement.streak_distribution}>
                    <XAxis dataKey="range" tick={{ fontSize: 9 }} /><YAxis hide />
                    <Tooltip /><Bar dataKey="count" fill="#F5831F" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Badge Activity */}
          <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
            <div className="flex items-center gap-2 mb-3"><Award size={18} style={{ color: '#7B4FD8' }} /><h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Badge & Achievement Activity</h3></div>
            <p className="text-2xl font-bold" style={{ color: '#1A1209' }}>{engagement.badges_this_week || 0}</p>
            <p className="text-xs" style={{ color: '#44403A' }}>Badges earned this week</p>
            {engagement.most_earned_badge && <p className="text-sm mt-2" style={{ color: '#44403A' }}>Most earned: {engagement.most_earned_badge}</p>}
            <p className="text-sm mt-1" style={{ color: '#44403A' }}>Virtual badges earned: {engagement.virtual_badges_earned || engagement.physical_badges_pending || 0}</p>
          </div>

          {/* Real-World Challenge Completions */}
          <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
            <div className="flex items-center gap-2 mb-3"><Target size={18} style={{ color: '#2EAA5C' }} /><h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Real-World Challenges</h3></div>
            <p className="text-2xl font-bold" style={{ color: '#1A1209' }}>{engagement.challenges_this_week || 0}</p>
            <p className="text-xs" style={{ color: '#44403A' }}>&quot;I Did This&quot; taps this week</p>
            {engagement.most_completed_challenge && <p className="text-sm mt-2" style={{ color: '#44403A' }}>Top: {engagement.most_completed_challenge}</p>}
            {engagement.challenge_trend && (
              <div className="mt-3 h-16">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={engagement.challenge_trend}>
                    <Line type="monotone" dataKey="count" stroke="#2EAA5C" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </InfoTooltipWrapper>

      {/* Row 5.5 — Self-Development Features */}
      <InfoTooltipWrapper persistent {...CC['self-development-features']} testId="self-development-features" id="self-development-dashboard-wrap">
        <h2 className="text-lg font-bold mb-3" style={{ color: '#1A1209' }}>Self-Development Features</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <MetricCard icon={Zap} label="Dares Completed" value={sd.dares_completed || 0} color="#EF4444" sub={`${sd.dares_this_period || 0} this period`} />
          <MetricCard icon={BookOpen} label="Journal Entries" value={sd.journal_entries || 0} color="#10B981" sub={`${sd.journal_this_period || 0} this period`} />
          <MetricCard icon={Target} label="Role-Plays Done" value={sd.roleplay_completions || 0} color="#8B5CF6" sub={`${sd.roleplay_this_period || 0} this period`} />
          <MetricCard icon={Users} label="Accountability Pods" value={sd.accountability_pods || 0} color="#3B82F6" sub={`${sd.active_pods || 0} active`} />
          <MetricCard icon={UserCheck} label="Active Mentorships" value={sd.active_mentorships || 0} color="#F59E0B" sub={`${sd.pod_checkins || 0} check-ins total`} />
          <MetricCard icon={MapPin} label="Real Meetups" value={sd.total_meetups || 0} color="#14B8A6" sub={`${sd.completed_meetups || 0} completed`} />
        </div>
      </InfoTooltipWrapper>

      {/* Row 6 — Safety & System Health */}
      <InfoTooltipWrapper persistent {...CC['safety-system-health']} testId="safety-system-health">
        <h2 className="text-lg font-bold mb-3" style={{ color: '#1A1209' }}>Safety & System Health</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Safety Summary */}
          <div className="bg-white p-5 rounded-lg cursor-pointer hover:shadow-md transition-all" style={{ border: '1px solid #E0E0E0' }} onClick={() => navigate('/admin/safety')}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><AlertTriangle size={18} style={{ color: '#DC2626' }} /><h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Safety Summary</h3></div>
              {overview.safety.guardrail_blocks > 0 && <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">{overview.safety.guardrail_blocks} events</span>}
            </div>
            <p className="text-2xl font-bold" style={{ color: '#1A1209' }}>{overview.safety.guardrail_blocks}</p>
            <p className="text-xs" style={{ color: '#44403A' }}>Guardrail triggers (30d)</p>
            {guardrails?.domain_breakdown && guardrails.domain_breakdown.length > 0 && (
              <div className="mt-3 space-y-1">
                {guardrails.domain_breakdown.slice(0, 4).map((d, i) => (
                  <div key={i} className="flex justify-between text-xs"><span style={{ color: '#44403A' }}>{d.domain}</span><span className="font-medium">{d.count}</span></div>
                ))}
              </div>
            )}
          </div>

          {/* System Health */}
          <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
            <div className="flex items-center gap-2 mb-3"><Activity size={18} style={{ color: '#2EAA5C' }} /><h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>System Health</h3></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { label: 'Database', value: `${health.db_response_ms}ms`, ok: health.db_response_ms < 100 },
                { label: 'Claude API', value: 'Active', ok: true },
                { label: 'Error Rate', value: `${health.error_rate_percent}%`, ok: health.error_rate_percent < 5 },
                { label: 'Users', value: health.collections?.users || 0, ok: true },
                { label: 'Sessions', value: health.collections?.sessions || 0, ok: true },
                { label: 'Payments', value: health.payments, ok: !String(health.payments).includes('mock') },
              ].map((m, i) => (
                <div key={i} className="p-2 rounded" style={{ backgroundColor: '#F9F9F9' }}>
                  <div className="flex items-center gap-1.5">
                    {m.ok ? <CheckCircle size={12} className="text-green-500" /> : <XCircle size={12} className="text-amber-500" />}
                    <span className="text-xs" style={{ color: '#44403A' }}>{m.label}</span>
                  </div>
                  <p className="text-sm font-medium mt-0.5" style={{ color: '#333333' }}>{m.value}</p>
                </div>
              ))}
            </div>
            <p className="text-xs mt-3" style={{ color: '#6B6257' }}>Last checked: {new Date().toLocaleTimeString()}</p>
          </div>
        </div>
      </InfoTooltipWrapper>
      </CommandCentreSection>

      <CommandCentreSection
        title="Growth & Leads"
        subtitle="Churn watch (membership is for life — every leaver is a win-back target), lead alerts, geographic demographics, and the lead-generation funnel. Where new humans are coming from and why members stay."
        accent="#0D9488"
        icon={<TrendingUp className="w-4 h-4" strokeWidth={2} />}
        testId="cc-section-growth-leads"
      >
      {/* ─── TIER 2: Positive Churn + Lead Alerts ──────────────────── */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Positive Churn */}
        <InfoTooltipWrapper persistent {...CC['positive-churn']} testId="positive-churn" marginBottom=""><div className="bg-white p-5 rounded-lg h-full" style={{ border: '1px solid #E0E0E0' }} data-testid="positive-churn-card">
          <div className="flex items-center gap-2 mb-4">
            <ThumbsUp size={18} style={{ color: '#2EAA5C' }} />
            <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Churn Watch</h3>
            <span className="ml-auto px-2 py-0.5 text-xs font-medium rounded-full" style={{ backgroundColor: '#ECFDF5', color: '#059669' }}>
              {positiveChurn.summary?.positive_rate || 0}% goal-complete
            </span>
          </div>
          <p className="text-xs mb-3" style={{ color: '#666' }}>Membership is for life — every inactive member is a win-back target. Goal-complete leavers get priority outreach.</p>
          <div className="flex gap-6 mb-4">
            <div>
              <p className="text-2xl font-bold" style={{ color: '#2EAA5C' }}>{positiveChurn.summary?.positive_count || 0}</p>
              <p className="text-xs" style={{ color: '#666' }}>Goal-complete</p>
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: '#6B6257' }}>{positiveChurn.summary?.standard_count || 0}</p>
              <p className="text-xs" style={{ color: '#666' }}>Standard Churn</p>
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: '#333' }}>{positiveChurn.summary?.total_churned || 0}</p>
              <p className="text-xs" style={{ color: '#666' }}>Total Churned</p>
            </div>
          </div>
          {positiveChurn.positive_churn?.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium" style={{ color: '#333' }}>Goal-complete leavers (win-back priority):</p>
              {positiveChurn.positive_churn.slice(0, 3).map((u, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded" style={{ backgroundColor: '#F9FBF9' }}>
                  <div>
                    <p className="text-xs font-medium" style={{ color: '#333' }}>{u.name || u.email}</p>
                    <p className="text-xs" style={{ color: '#6B6257' }}>{u.total_sessions} sessions, {u.total_challenges} challenges, {u.total_badges} badges</p>
                  </div>
                  <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: '#ECFDF5', color: '#059669' }}>{u.criteria_met}/4</span>
                </div>
              ))}
            </div>
          )}
        </div></InfoTooltipWrapper>

        {/* Lead Alerts */}
        <InfoTooltipWrapper persistent {...CC['lead-monitoring']} testId="lead-monitoring" marginBottom=""><div className="bg-white p-5 rounded-lg h-full" style={{ border: '1px solid #E0E0E0' }} data-testid="lead-alerts-card">
          <div className="flex items-center gap-2 mb-4">
            <Bell size={18} style={{ color: '#F5831F' }} />
            <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Lead Monitoring</h3>
            {(leadAlerts.stats?.leads_24h || 0) > 0 && (
              <span className="ml-auto flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full" style={{ backgroundColor: '#FFF7ED', color: '#C2410C' }}>
                <Zap size={10} /> {leadAlerts.stats.leads_24h} new today
              </span>
            )}
          </div>
          {/* Alerts list */}
          {leadAlerts.alerts?.length > 0 ? (
            <div className="space-y-2 mb-4">
              {leadAlerts.alerts.map((alert, i) => {
                const alertColors = { info: { bg: '#EFF6FF', text: '#1D4ED8', Icon: Info }, success: { bg: '#ECFDF5', text: '#059669', Icon: CheckCircle }, warning: { bg: '#FFFBEB', text: '#B45309', Icon: TriangleAlert } };
                const ac = alertColors[alert.type] || alertColors.info;
                const AIcon = ac.Icon;
                return (
                  <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg" style={{ backgroundColor: ac.bg }}>
                    <AIcon size={14} style={{ color: ac.text, marginTop: 2 }} />
                    <p className="text-xs" style={{ color: ac.text }}>{alert.message}</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs py-2 mb-4" style={{ color: '#6B6257' }}>No alerts — all looking good.</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div className="p-2.5 rounded" style={{ backgroundColor: '#F9F9F9' }}>
              <p className="text-xs" style={{ color: '#666' }}>Leads (7d)</p>
              <p className="text-lg font-bold" style={{ color: '#1A1209' }}>{leadAlerts.stats?.leads_7d || 0}</p>
            </div>
            <div className="p-2.5 rounded" style={{ backgroundColor: '#F9F9F9' }}>
              <p className="text-xs" style={{ color: '#666' }}>Waitlist Total</p>
              <p className="text-lg font-bold" style={{ color: '#1A1209' }}>{leadAlerts.stats?.waitlist_total || 0}</p>
            </div>
            <div className="p-2.5 rounded" style={{ backgroundColor: '#F9F9F9' }} data-testid="charter-downloads-stat">
              <p className="text-xs" style={{ color: '#666' }}>Charter Downloads</p>
              <p className="text-lg font-bold" style={{ color: '#1A1209' }}>{leadAlerts.stats?.charter_downloads || 0}</p>
            </div>
          </div>
          {leadAlerts.top_keywords?.length > 0 && (
            <div>
              <p className="text-xs font-medium mb-2" style={{ color: '#333' }}>Top search keywords:</p>
              <div className="flex flex-wrap gap-1.5">
                {leadAlerts.top_keywords.map((kw, i) => (
                  <span key={i} className="px-2 py-1 rounded-full text-xs" style={{ backgroundColor: '#FFF7ED', color: '#C2410C' }}>
                    {kw.keyword} ({kw.count})
                  </span>
                ))}
              </div>
            </div>
          )}
        </div></InfoTooltipWrapper>
      </div>

      {/* ─── White Paper Download Ledger ──────────────────────── */}
      <div className="mb-6">
        <WhitePaperLedgerCard />
      </div>

      {/* ─── TIER 3: Geographic Dashboard ──────────────────────── */}
      <InfoTooltipWrapper persistent {...CC['user-demographics']} testId="user-demographics" id="geographic-dashboard-wrap">
        <h2 className="text-lg font-bold mb-3" style={{ color: '#1A1209' }}>User Demographics</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {/* Region Distribution */}
          <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
            <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>By Region (email TLD)</h3>
            {geography.by_region?.length > 0 ? (
              <div className="space-y-2">
                {geography.by_region.slice(0, 8).map((r, i) => {
                  const pct = geography.total_users > 0 ? Math.round((r.count / geography.total_users) * 100) : 0;
                  const colors = ['#F5831F', '#7B4FD8', '#2563EB', '#2EAA5C', '#DC2626', '#F59E0B', '#EC4899', '#06B6D4'];
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-xs" style={{ color: '#333' }}>{r.region}</span>
                        <span className="text-xs font-medium" style={{ color: '#666' }}>{r.count} ({pct}%)</span>
                      </div>
                      <div className="w-full h-1.5 rounded-full" style={{ backgroundColor: '#F0F0F0' }}>
                        <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, backgroundColor: colors[i % colors.length], minWidth: pct > 0 ? '4px' : '0' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs py-4 text-center" style={{ color: '#6B6257' }}>No region data yet</p>
            )}
          </div>

          {/* Role Distribution */}
          <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
            <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>By Role</h3>
            {geography.by_role?.length > 0 ? (
              <div>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={geography.by_role.map((r, i) => ({ name: r.role, value: r.count, fill: ['#F5831F', '#7B4FD8', '#2563EB', '#2EAA5C', '#DC2626'][i % 5] }))} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                      {geography.by_role.map((_, i) => <Cell key={i} fill={['#F5831F', '#7B4FD8', '#2563EB', '#2EAA5C', '#DC2626'][i % 5]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-2 mt-2 justify-center">
                  {geography.by_role.map((r, i) => (
                    <span key={i} className="flex items-center gap-1 text-xs" style={{ color: '#666' }}>
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ['#F5831F', '#7B4FD8', '#2563EB', '#2EAA5C', '#DC2626'][i % 5] }} />
                      {r.role} ({r.count})
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-xs py-4 text-center" style={{ color: '#6B6257' }}>No role data yet</p>
            )}
          </div>

          {/* Age Band Distribution */}
          <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
            <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>By Age Band</h3>
            {geography.by_age_band?.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={geography.by_age_band} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="age_band" tick={{ fill: '#666', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#666', fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#7B4FD8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-xs py-4 text-center" style={{ color: '#6B6257' }}>No age data yet</p>
            )}
          </div>
        </div>
      </InfoTooltipWrapper>

      {/* Row 3 — Lead Generation Summary */}
      <InfoTooltipWrapper persistent {...CC['lead-generation']} testId="lead-generation">
        <h2 className="text-lg font-bold mb-3" style={{ color: '#1A1209' }}>Lead Generation</h2>
        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <div className="flex items-center justify-between">
            <div className="flex gap-8">
              <div>
                <p className="text-xs" style={{ color: '#44403A' }}>Leads This Week</p>
                <p className="text-2xl font-bold" style={{ color: '#1A1209' }}>{leads.total_this_week}</p>
              </div>
              <div>
                <p className="text-xs" style={{ color: '#44403A' }}>Qualified Leads</p>
                <p className="text-2xl font-bold" style={{ color: '#1A1209' }}>{leads.qualified}</p>
              </div>
              <div>
                <p className="text-xs" style={{ color: '#44403A' }}>Conversion Rate</p>
                <p className="text-2xl font-bold" style={{ color: '#1A1209' }}>{leads.conversion_rate}%</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-xs">
                <span className={`w-2 h-2 rounded-full ${leads.connection_status === 'connected' ? 'bg-green-500' : leads.connection_status === 'degraded' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                <span style={{ color: '#44403A' }}>Reddit API: {leads.connection_status}</span>
              </div>
              <button onClick={() => navigate('/admin/leads')} className="px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: '#F5831F' }}>View Lead Dashboard</button>
            </div>
          </div>
          {leads.sources && leads.sources.length > 0 && (
            <div className="mt-4 h-32">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={leads.sources} layout="vertical">
                  <XAxis type="number" /><YAxis type="category" dataKey="source" width={80} tick={{ fontSize: 11 }} />
                  <Tooltip /><Bar dataKey="count" fill="#F5831F" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </InfoTooltipWrapper>
      </CommandCentreSection>

      <CommandCentreSection
        title="Stability & Health"
        subtitle="Payment stack, nightly CI heartbeat, live email-send canary, and per-service uptime — everything that must stay green for the platform to earn its keep."
        accent="#DC2626"
        icon={<HeartPulse className="w-4 h-4" strokeWidth={2} />}
        testId="cc-section-stability-health"
      >
        {/* Feb 2026 rebalance — Robert flagged a large white gap under
            the right column because Platform Health didn't visually
            balance five stacked cards on the left. New layout:
              LEFT  → Stripe Live → Postmark Budget (both wide utility
                       cards, roughly equal to Platform Health's height)
              RIGHT → Platform Health → CI Health Badge → Pytest Flake
                       card → Monday Pulse test button (the four compact
                       tiles that used to bloat the left column).
            Result: two visually balanced columns, no more dead space. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="flex flex-col gap-4">
            <InfoTooltipWrapper persistent {...CC['stripe-live-banner']} testId="stripe-live-banner" marginBottom=""><StripeLiveBanner /></InfoTooltipWrapper>
            <PostmarkBudgetCard />
          </div>
          <div className="flex flex-col gap-4">
            <InfoTooltipWrapper persistent {...CC['platform-health']} testId="platform-health" marginBottom=""><PlatformHealthCard /></InfoTooltipWrapper>
            <InfoTooltipWrapper persistent {...CC['ci-health-badge']} testId="ci-health-badge-wrapper" marginBottom="">
              <CIHealthBadge />
            </InfoTooltipWrapper>
            <PytestFlakeCard />
            <TestMondayEmailButton />
          </div>
        </div>
      </CommandCentreSection>

      <CommandCentreSection
        title="Stabilisation Register"
        subtitle="Every production issue we've raised, worked, or closed. The single source of truth for what's broken and who's fixing it."
        accent="#4F46E5"
        icon={<Wrench className="w-4 h-4" strokeWidth={2} />}
        testId="cc-section-stabilisation"
      >
        <InfoTooltipWrapper persistent {...CC['stabilisation-log']} testId="stabilisation-log">
          <StabilisationLog />
        </InfoTooltipWrapper>
      </CommandCentreSection>

      <CommandCentreSection
        title="Growth Signal"
        subtitle="Trust-report engagement, live user traction, investor pipeline heatmap, and the pulse scheduler for time-sensitive VC outreach."
        accent="#059669"
        icon={<TrendingUp className="w-4 h-4" strokeWidth={2} />}
        testId="cc-section-growth-signal"
      >
        <div data-testid="trust-report-engagement-slot">
          <TrustReportEngagementCard />
        </div>
        <InfoTooltipWrapper persistent {...CC['live-traction']} testId="live-traction">
          <LiveTractionCard surface="admin" />
        </InfoTooltipWrapper>
        <InfoTooltipWrapper persistent {...CC['prospect-alerts']} testId="prospect-alerts">
          <ProspectAlertsCard />
        </InfoTooltipWrapper>
        <InfoTooltipWrapper persistent {...CC['investor-heatmap']} testId="investor-heatmap">
          <InvestorHeatmapCard />
        </InfoTooltipWrapper>
        <InfoTooltipWrapper persistent id="cc-investor-pulse" {...CC['investor-pulse-scheduler']} testId="investor-pulse-scheduler">
          <InvestorPulseSchedulerCard />
        </InfoTooltipWrapper>
      </CommandCentreSection>

      <CommandCentreSection
        title="Product & AvA Behaviour"
        subtitle="Maya, Rebecca, the AvA prompt studio, template firing counts, launch funnel telemetry, privacy-shield hits, and the triggered-but-declined handover watch. The safety layer of the whole product."
        accent="#7C3AED"
        icon={<Sparkles className="w-4 h-4" strokeWidth={2} />}
        testId="cc-section-product-ava"
      >
        <InfoTooltipWrapper persistent {...CC['maya-manager']} testId="maya-manager">
          <MayaManagerTile />
        </InfoTooltipWrapper>
        <InfoTooltipWrapper persistent id="cc-rebecca-manager" {...CC['rebecca-manager']} testId="rebecca-manager">
          <RebeccaManagerTile />
        </InfoTooltipWrapper>
        <InfoTooltipWrapper persistent id="cc-neo-manager" {...CC['neo-manager']} testId="neo-manager">
          <NeoManagerTile />
        </InfoTooltipWrapper>
        <InfoTooltipWrapper persistent id="cc-ava-behavior" {...CC['ava-behavior-studio']} testId="ava-behavior-studio">
          <AvABehaviorStudio />
        </InfoTooltipWrapper>
        <InfoTooltipWrapper persistent {...CC['templates-fired']} testId="templates-fired">
          <TemplatesFiredCard />
        </InfoTooltipWrapper>
        <InfoTooltipWrapper persistent {...CC['active-launch-funnel']} testId="active-launch-funnel">
          <ActiveLaunchFunnelCard />
        </InfoTooltipWrapper>
        <InfoTooltipWrapper persistent {...CC['privacy-shield-hits']} testId="privacy-shield-hits">
          <PrivacyShieldHitsCard />
        </InfoTooltipWrapper>
        <InfoTooltipWrapper persistent id="cc-safety-guardrails" {...CC['triggered-declined']} testId="triggered-declined">
          <TriggeredDeclinedTile />
        </InfoTooltipWrapper>
      </CommandCentreSection>

      <CommandCentreSection
        title="Community & Voice"
        subtitle="Peer Helps, the Proactive Champions leaderboard, and the raw stream of teen/adult ideas + unmatched searches. The pulse of who's engaged and what they're asking for."
        accent="#0891B2"
        icon={<Heart className="w-4 h-4" strokeWidth={2} />}
        testId="cc-section-community-voice"
      >
        <RealWorldActivityPulseCard />
        <InfoTooltipWrapper persistent {...CC['peer-helps']} testId="peer-helps">
          <PeerHelpsCard />
        </InfoTooltipWrapper>
        <InfoTooltipWrapper persistent id="cc-proactivity-leaderboard" {...CC['proactivity-leaderboard']} testId="proactivity-leaderboard">
          <ProactivityLeaderboardTile />
        </InfoTooltipWrapper>
        <InfoTooltipWrapper persistent id="cc-voice-of-user" {...CC['voice-of-user']} testId="voice-of-user">
          <VoiceOfUserCard />
        </InfoTooltipWrapper>
      </CommandCentreSection>

      <CommandCentreSection
        title="Newsletter & Comms"
        subtitle="Compose, queue, and schedule the Resilience Newsletter cadence, and watch Postmark suppressions so bounces never re-fire."
        accent="#D97706"
        icon={<Megaphone className="w-4 h-4" strokeWidth={2} />}
        testId="cc-section-newsletter-comms"
      >
        <InfoTooltipWrapper persistent id="cc-newsletter-ops" {...CC['newsletter-operations']} testId="newsletter-operations">
          <NewsletterOperationsCard />
        </InfoTooltipWrapper>
        <InfoTooltipWrapper persistent id="cc-newsletter-stack" {...CC['newsletter-queue-stack']} testId="newsletter-queue-stack">
          <NewsletterQueueStack
            onLoadIntoEditor={(draft) => {
              window.dispatchEvent(new CustomEvent('hibff:newsletter:load-into-editor', { detail: draft }));
              const el = document.getElementById('cc-newsletter-ops');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          />
        </InfoTooltipWrapper>
        <InfoTooltipWrapper persistent id="cc-newsletter-calendar" {...CC['newsletter-annual-calendar']} testId="newsletter-annual-calendar">
          <NewsletterAnnualCalendar
            onSlotClick={(tile) => {
              const el = document.getElementById('cc-newsletter-stack');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          />
        </InfoTooltipWrapper>
        <InfoTooltipWrapper persistent id="cc-suppressions" {...CC['postmark-suppressions']} testId="postmark-suppressions">
          <PostmarkSuppressionsTile />
        </InfoTooltipWrapper>
      </CommandCentreSection>

      <CommandCentreSection
        title="Founder & Board"
        subtitle="Master-admin-only. The Founder's Letter editor and the monthly Board Deck."
        accent="#B45309"
        icon={<Compass className="w-4 h-4" strokeWidth={2} />}
        testId="cc-section-founder-board"
      >
        <InfoTooltipWrapper persistent {...CC['founder-letter-editor']} testId="founder-letter-editor">
          <FounderLetterEditor />
        </InfoTooltipWrapper>
        <InfoTooltipWrapper persistent {...CC['board-deck']} testId="board-deck">
          <BoardDeckCard />
        </InfoTooltipWrapper>
      </CommandCentreSection>

      <CommandCentreSection
        title="Security & Admin Ops"
        subtitle="Security posture, recent admin activity, adult-vs-teen audience split, and the deep-dashboard headline metrics."
        accent="#334155"
        icon={<ShieldCheck className="w-4 h-4" strokeWidth={2} />}
        testId="cc-section-security-admin"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <InfoTooltipWrapper persistent {...CC['security-status']} testId="security-status" marginBottom=""><SecurityStatusCard /></InfoTooltipWrapper>
          <InfoTooltipWrapper persistent {...CC['recent-admin-activity']} testId="recent-admin-activity" marginBottom=""><RecentAdminActivityCard /></InfoTooltipWrapper>
        </div>
        <InfoTooltipWrapper persistent {...CC['persona-split']} testId="persona-split">
          <PersonaSplitTile />
        </InfoTooltipWrapper>
      </CommandCentreSection>
      </div>
    </AdminLayout>
  );
};

export default AdminHome;
